import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { ProductTile } from '../shell/Sidebar';

const LOGO_PX = 128; // uploaded images are scaled to fit this square
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_SVG_BYTES = 100 * 1024;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

// Raster images are drawn into a small square PNG so the logo stays light;
// SVGs are kept as they are, since they scale cleanly
const fileToLogo = async (file) => {
    if (!/^image\/(png|jpeg|webp|gif|svg\+xml)$/.test(file.type)) throw new Error('Use a PNG, JPG, WebP, GIF or SVG image.');
    if (file.size > MAX_UPLOAD_BYTES) throw new Error('That image is over 5 MB. Try a smaller one.');
    const dataUrl = await readAsDataUrl(file);
    if (file.type === 'image/svg+xml') {
        if (file.size > MAX_SVG_BYTES) throw new Error('That SVG is over 100 KB. Try a PNG instead.');
        return dataUrl;
    }
    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Couldn't open that image."));
        img.src = dataUrl;
    });
    const scale = Math.min(1, LOGO_PX / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    // Centred on a square canvas, so wide logos aren't stretched in the tile
    const side = Math.max(width, height);
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    canvas.getContext('2d').drawImage(image, (side - width) / 2, (side - height) / 2, width, height);
    return canvas.toDataURL('image/png');
};

// The product tile with a menu to upload a logo, re-fetch the website's icon or remove it
const LogoPicker = ({ product, onSave, size = 52, radius = 13, fontSize = 22 }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileInput = useRef(null);
    const root = useRef(null);

    const webSource = (product.sources || []).find(source => ['landing', 'store'].includes(source.type) && source.url);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => {
            if (event.type === 'keydown' ? event.key === 'Escape' : !root.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', close);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', close);
        };
    }, [open]);

    const run = async (action) => {
        setOpen(false);
        setBusy(true);
        setError('');
        try {
            await action();
        } catch (e) {
            setError(e.response?.data?.error || e.message || "Couldn't change the logo. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const upload = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) run(async () => onSave(await fileToLogo(file)));
    };

    const useWebsiteIcon = () => run(async () => {
        const { icon } = await api.findProductIcon(webSource.type, webSource.url);
        if (!icon) throw new Error("Couldn't find an icon on your website.");
        await onSave(icon);
    });

    return (
        <div className="ls-logo-picker" ref={root}>
            <button
                type="button"
                className="ls-logo-button"
                onClick={() => setOpen(!open)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={product.logo ? 'Change logo' : 'Add a logo'}
                title={product.logo ? 'Change logo' : 'Add a logo'}
                disabled={busy}
            >
                <ProductTile product={product} size={size} radius={radius} fontSize={fontSize} />
                <span className="ls-logo-badge" aria-hidden="true"><Icon name="edit" size={13} /></span>
                {busy && <span className="ls-logo-busy"><span className="ls-spinner" /></span>}
            </button>

            {open && (
                <div className="ls-popover ls-logo-menu" role="menu">
                    <div className="ls-popover-title">Logo</div>
                    <button type="button" role="menuitem" className="ls-popover-row" onClick={() => fileInput.current?.click()}>
                        <Icon name="upload" size={18} />Upload an image
                    </button>
                    {webSource && (
                        <button type="button" role="menuitem" className="ls-popover-row" onClick={useWebsiteIcon}>
                            <Icon name="language" size={18} />Use {webSource.type === 'store' ? 'the app icon' : 'the website icon'}
                        </button>
                    )}
                    {product.logo && (
                        <>
                            <div className="ls-popover-divider" />
                            <button type="button" role="menuitem" className="ls-popover-row" onClick={() => run(() => onSave(''))}>
                                <Icon name="hide_image" size={18} />Remove logo
                            </button>
                        </>
                    )}
                </div>
            )}

            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={upload} />
            {error && <div className="ls-hint" role="alert" style={{ color: 'var(--danger-text)', maxWidth: 200, marginTop: 8 }}>{error}</div>}
        </div>
    );
};

export default LogoPicker;
